/**
 * Hash routing, in a couple of hundred lines.
 *
 * No router library. The whole route space is `#/view` and `#/view/section` —
 * two levels, twelve views — and a dependency that can express nested layouts and
 * data loaders would be more configuration than the thing it configures.
 *
 * Hash rather than history paths, deliberately: the server serves one HTML file
 * and has no catch-all for arbitrary paths, so `/books` would 404 on a hard
 * reload while `#/books` cannot.
 *
 * `section` is whatever the view's second level is — a settings section, a
 * shelf, a stack. Putting it in the address is what makes a view's inner state
 * linkable and Back meaningful: without it, switching shelf changes the screen
 * and leaves the address describing something else.
 *
 * The store is hand-rolled rather than a bare `hashchange` listener because
 * navigation is announced *synchronously*. Setting `location.hash` fires
 * `hashchange` on a later task, which is too late for a view transition: the
 * browser captures the new state when the callback returns, and the callback
 * would return before React had heard about anything. `pushState` plus an
 * explicit notify gives one synchronous point where the route changes.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { withTransition } from "./transition.js";

export const VIEWS = [
  "home",
  "feed",
  "books",
  "papers",
  "calendar",
  "training",
  "health",
  "split",
  "files",
  "stacks",
  "projects",
  "settings",
];
const DEFAULT_VIEW = "home";

/*
 * The views of the modules this page loaded, which `parse` accepts beside
 * Wire's own. Filled before the shell first renders, so an address naming one
 * on a cold load is read as that view rather than rewritten to home before the
 * module that draws it has arrived.
 */
const moduleViews = new Set();
const known = (view) => VIEWS.includes(view) || moduleViews.has(view);

/**
 * Accept a loaded module's views, and return the ones accepted.
 *
 * Never a name Wire already routes: its own views, and the old ones `migrate`
 * reads, which an address would go on reaching before any module could.
 */
export function addViews(names) {
  const accepted = names.filter((name) => !VIEWS.includes(name) && migrate(name, []) === null);
  for (const name of accepted) moduleViews.add(name);
  return accepted;
}

/*
 * Where an address from before Projects now points.
 *
 * One project used to live in two views, Agents and Roadmaps, and both handed
 * out links: in issues, in roadmaps, in bookmarks, in messages people sent
 * each other. Those are a promise, so each is read as the place it meant and
 * written back as the new address, with `replace` (see `useRoute`), so Back
 * does not walk into the old one again.
 *
 * Agents named its lenses after what they showed and its pages with a letter:
 * `i12` an issue, `m14` a claim. Projects names them as tabs and numbers
 * under them, so the two kinds of page cannot be confused by a letter.
 *
 * `#/roadmaps/<id>` is the one this cannot finish on its own: a roadmap's
 * project is not in its address, and only the list of roadmaps knows it. It
 * comes back as the Projects index carrying `legacyRoadmap`, and the view
 * writes the real address once it has the list.
 */
const LEGACY_LENSES = {
  issues: "issues",
  board: "board",
  roadmap: "roadmaps",
  memory: "memory",
  sessions: "agents",
  review: "review",
};

function migrate(view, parts) {
  if (view === "agents") {
    const [name, lens, ...rest] = parts;
    if (!name) return { view: "projects", section: null };
    if (/^i[0-9]+$/.test(lens || "")) return { view: "projects", section: `${name}/issues/${lens.slice(1)}` };
    if (/^m[0-9]+$/.test(lens || "")) return { view: "projects", section: `${name}/memory/${lens.slice(1)}` };
    const tab = LEGACY_LENSES[lens];
    if (!tab) return { view: "projects", section: name };
    // The board kept what it was narrowed to, `board/r3/m7`, and still does.
    const kept = tab === "board" ? rest : [];
    return { view: "projects", section: [name, tab, ...kept].join("/") };
  }
  if (view === "roadmaps") {
    const [what] = parts;
    if (!what) return { view: "projects", section: null };
    // A number was a roadmap first, as Roadmaps read it: a project whose
    // name is a roadmap's id was always the one that lost.
    if (/^[0-9]+$/.test(what)) return { view: "projects", section: null, legacyRoadmap: Number(what) };
    return { view: "projects", section: `${what}/roadmaps` };
  }
  return null;
}

/** The inverse of `toHash`'s encoding. Malformed input decodes to itself. */
function decode(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    // `%zz` is not a valid escape and `decodeURIComponent` throws on it. An
    // address somebody mistyped should resolve to a section that does not
    // exist — and be redirected — rather than take the view down with it.
    return segment;
  }
}

export function parse(hash = window.location.hash) {
  const [view, ...rest] = hash.replace(/^#\/?/, "").split("/");
  const moved = migrate(view, rest.filter(Boolean).map(decode));
  if (moved) {
    // Not exact, so it is written back as the new address — except the one
    // old roadmap address the view has to look up, which would be written
    // back as the bare index and lose the roadmap it named.
    return { legacyRoadmap: null, ...moved, exact: moved.legacyRoadmap != null };
  }
  return {
    legacyRoadmap: null,
    view: known(view) ? view : DEFAULT_VIEW,
    // Decoded, because `toHash` encodes. Without this the pair did not round
    // trip: a section with a space went out as `web%20api` and came back as
    // the literal `"web%20api"`, so it matched nothing in the list it was
    // supposed to select and was redirected away.
    //
    // Joined rather than taken at [1] for the same reason: `encodeURIComponent`
    // turns a slash into `%2F`, so a section containing one arrives as a single
    // segment — but an address typed by hand may still split, and treating the
    // rest as one section is what makes both readings agree.
    section: rest.filter(Boolean).map(decode).join("/") || null,
    // Whether the address actually said what `parse` returned. The views use
    // this to write a resolved route back, so what is on screen and what is
    // linkable never disagree.
    exact: known(view),
  };
}

/**
 * Encoded segment by segment, so a slash inside a section stays a slash.
 *
 * `encodeURIComponent` on the whole thing turns it into `%2F`, and the calendar's
 * `week/2026-08-03` went out as `#/calendar/week%2F2026-08-03` — which parses
 * back correctly and reads like a mistake. Both forms still round-trip, because
 * `parse` decodes each segment and joins them again; this one is legible in the
 * address bar and in a link somebody pastes into a message.
 */
export function toHash({ view, section }) {
  if (!section) return `#/${view}`;
  const path = String(section).split("/").map(encodeURIComponent).join("/");
  return `#/${view}/${path}`;
}

// --------------------------------------------------------------------------
// The store
// --------------------------------------------------------------------------

/*
 * Two markers, not one, and the difference matters.
 *
 * `told` is what the subscribers have been given — the snapshot React renders
 * from. `taken` is the hash this module has already decided to move to.
 *
 * They differ because `startViewTransition` does not run its callback
 * synchronously: it captures the old frame first and calls back a moment
 * later. With one marker, the second of the two events a history traversal
 * fires would still see the old hash, start a second transition, and abort the
 * first — which looked precisely like having no transition at all. Claiming
 * the hash the instant the decision is made closes that window, while React
 * goes on rendering the old route until the callback actually runs, which is
 * what gives the browser something to animate away from.
 */
let told = typeof window === "undefined" ? "" : window.location.hash;
let taken = told;
const listeners = new Set();

function claim() {
  if (window.location.hash === taken) return false;
  taken = window.location.hash;
  return true;
}

function announce() {
  told = taken;
  for (const listener of listeners) listener();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => told;

/*
 * Which way a traversal went.
 *
 * `popstate` says the history moved but not whether it moved back or forward,
 * and that is the only thing the gesture actually means — so each entry this
 * module writes carries a counter, and comparing counters answers it. Without
 * this, Back and forward would animate identically and the motion would be
 * decoration rather than information.
 */
let depth = 0;

/*
 * How many times the browser, rather than this module, moved the address.
 *
 * A navigation is decided now and written later: `withTransition` hands its
 * update to `startViewTransition`, which calls back after the next frame. If
 * the reader presses Back inside that gap, `replaceState` lands on the entry
 * Back arrived at. Settings' redirect from `#/settings` to
 * `#/settings/appearance` did exactly that to `#/feed/24h`, so Back showed
 * Settings again and the feed's filters were gone from the history (#1754).
 *
 * So a navigation remembers this count when it is decided and is dropped if
 * it has moved by the time the update runs: it was a decision about an
 * address the reader has already left. Counted only when `claim` succeeds, so
 * the popstate and hashchange of one traversal count once, and never by
 * `stamp`, so two navigations this module makes in a row both apply.
 */
let moves = 0;

function stamp(url, { push }) {
  if (push) depth += 1;
  const state = { wire: depth };
  if (push) history.pushState(state, "", url);
  else history.replaceState(state, "", url);
}

if (typeof window !== "undefined") {
  const existing = history.state?.wire;
  depth = typeof existing === "number" ? existing : 0;
  // The entry the app loaded into may predate this counter — a reload, or an
  // address pasted in. Give it one without touching the URL or the history
  // length, so the first Back has something to compare against.
  if (typeof existing !== "number") {
    history.replaceState({ wire: depth }, "", window.location.href);
  }

  window.addEventListener("popstate", (event) => {
    const to = typeof event.state?.wire === "number" ? event.state.wire : depth;
    const direction = to < depth ? "back" : "forward";
    depth = to;
    if (!claim()) return;
    moves += 1;
    withTransition(announce, { direction });
  });

  // An address typed or pasted into the bar, which is neither back nor
  // forward: no direction, so it gets the plain animation.
  //
  // `claim` is what keeps this from undoing the one above: traversing a
  // same-document hash entry fires *both* events, and a second transition
  // aborts the one already running.
  window.addEventListener("hashchange", () => {
    if (!claim()) return;
    moves += 1;
    withTransition(announce);
  });
}

/**
 * The current route, plus the two ways to change it.
 *
 * `go` pushes, so Back returns to where you were — that is what a click on a
 * destination means. `replace` does not, which is what a redirect wants: being
 * bounced off an unknown section should not leave a trap in the history that
 * Back walks straight back into.
 */
export function useRoute() {
  const hash = useSyncExternalStore(subscribe, snapshot, () => "");
  const route = parse(hash);

  const navigate = useCallback((view, section, { push, direction, still = false }) => {
    const next = toHash({ view, section: section ?? null });
    // Nothing to do, and nothing to animate: a click on the destination you
    // are already looking at should not push an entry or start a transition.
    if (window.location.hash === next) return;

    // `pushState`/`replaceState` rather than assigning `location.hash`,
    // because neither fires an event — so this path cannot collide with the
    // listeners above, and the notify is under our control.
    const decided = moves;
    const update = () => {
      if (moves !== decided) return;
      stamp(next, { push });
      claim();
      announce();
    };
    /* `still` is a choice made inside a screen rather than a move to another
       one — which milestone of a plan is open. It is written down, so a
       reload and Back agree with what is on screen, and not animated: the
       whole pane fading for every arrow key through a row of milestones was
       motion that said nothing. */
    if (still) update();
    else withTransition(update, { direction });
  }, []);

  const go = useCallback(
    (view, section = null, options = {}) => navigate(view, section, { ...options, push: true }),
    [navigate],
  );

  const replace = useCallback(
    (view, section = null, { still = false } = {}) =>
      navigate(view, section, { push: false, still }),
    [navigate],
  );

  // An address with no route at all, or one naming a view that does not exist,
  // is written down rather than merely rendered: otherwise the dashboard shows
  // while the address bar still says `#/nonsense`, the first Back press cycles
  // instead of leaving, and copying the address links to nothing.
  useEffect(() => {
    if (!window.location.hash || !parse().exact) {
      stamp(toHash({ view: route.view, section: route.section }), { push: false });
      claim();
      announce();
    }
  }, [route.view, route.section]);

  return { ...route, go, replace };
}

/**
 * Keep one view's section legal.
 *
 * Every view with a second level has the same two problems: an address may
 * name a section that does not exist, and an address may name none at all.
 * Both resolve to the default, and both get written back — with `replace`, so
 * a bad link does not become a history entry to fall back into.
 */
export function useSection(view, allowed, fallback) {
  const { view: here, section, replace } = useRoute();
  const known = section !== null && allowed.includes(section);

  useEffect(() => {
    if (here === view && !known && fallback) replace(view, fallback);
  }, [here, view, known, fallback, replace]);

  return known ? section : fallback;
}
